import Link from "next/link";
import type { ReactNode } from "react";
import { HubArrow, HubChevron } from "@/app/_components/conditions/hub/hub-icons";
import type { AreaKind } from "@/lib/areas/types";
import type {
  AreaViewAward,
  AreaViewBestMonths,
  AreaViewBestTime,
  AreaViewMonthlyCurve,
  AreaViewSeasonWeek,
  AreaViewSportAwards,
  AreaViewUnit,
} from "../area-view-types";
import {
  G3ActionLink,
  G3CardHeader,
  G3GroupLabel,
  G3Icon,
  dayMonthLong,
  joinList,
  monthLong,
  monthShort,
  singularUnit,
} from "./g3-parts";

/**
 * "The PeakWave record" and "Awards won here" (G3, following the sport toggle since G4): when
 * the area is at its best for one sport. Coast areas show the published best months (top three,
 * plus the 12-month strip and the spot most often best when the insights carry them) or an honest
 * empty state, and wind direction winners when more than one beach wins. Mountain areas add the
 * best-time curves from the older weekly scores (labelled as such), last winter's crowds and
 * 6-day pass prices week by week, and last season's summary. Client-safe bodies: the page wraps
 * one per sport in its record and awards sections (AreaPageShell), and the on-demand sport panels
 * render the same bodies from the panel route's payload.
 */

const CURVE_COLOUR: Readonly<Record<AreaViewMonthlyCurve["sport"], string>> = { piste: "#3E5470", mtb: "#16808F" };
const CURVE_TILE_LABEL: Readonly<Record<AreaViewMonthlyCurve["sport"], string>> = { piste: "Best for skiing", mtb: "Best for MTB" };
const MONTH_INITIALS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

// ── Best months (insights) ───────────────────────────────────────────────

function BestMonthsList({ data, areaName, unit }: Readonly<{ data: AreaViewBestMonths; areaName: string; unit: AreaViewUnit }>) {
  const top = data.months.slice(0, 3);
  const thing = singularUnit(unit);
  return (
    <>
      <h3 className="mx-4 mt-2.5 text-[15px] font-bold text-navy md:mx-[22px] md:mt-1 md:text-[16px]">Best months for {data.sportLabel}</h3>
      <ol
        aria-label={`Best months for ${data.sportLabel} in ${areaName}, top ${top.length}`}
        className="mx-4 mt-2.5 flex flex-col gap-1.5 md:mx-[22px] md:mt-3 md:grid md:grid-cols-3 md:gap-3.5"
      >
        {top.map((month, index) => {
          const lead = index === 0;
          const share = `${month.share70Pct}% of days had a ${thing} at 70+`;
          return (
            <li
              key={month.month}
              data-area-best-month={month.month}
              className={`flex min-h-[60px] items-center gap-2.5 rounded-[14px] border px-3 py-2.5 md:flex-col md:items-stretch md:gap-1 md:rounded-[16px] md:px-[18px] md:py-4 ${
                lead ? "border-navy bg-navy" : "border-line bg-warm-bg"
              }`}
            >
              <span className="flex min-w-0 flex-1 items-center gap-2.5 md:flex-none md:gap-2">
                <span
                  className={`flex size-6 shrink-0 items-center justify-center rounded-full text-[12px] font-bold md:text-[12.5px] ${
                    lead ? "bg-teal-light text-navy" : "bg-[#E4F2F3] text-teal-dark"
                  }`}
                >
                  {index + 1}
                </span>
                <span className="flex min-w-0 flex-col gap-px">
                  <span className={`text-[15px] font-bold md:text-[17px] ${lead ? "text-white" : "text-navy"}`}>{month.name}</span>
                  <span className={`text-[12px] leading-[1.3] md:hidden ${lead ? "text-[#C9D3E0]" : "text-[#566072]"}`}>{share}</span>
                </span>
              </span>
              <span className="flex shrink-0 flex-col items-end md:mt-2 md:items-start">
                <span className={`text-[24px] font-bold leading-none md:text-[40px] ${lead ? "text-white" : "text-navy"}`}>
                  {month.score100}
                  <span className={`text-[12px] font-semibold md:text-[15px] ${lead ? "text-[#C9D3E0]" : "text-[#566072]"}`}>/100</span>
                </span>
                <span className={`text-[11px] md:text-[13px] ${lead ? "text-[#C9D3E0]" : "text-[#566072]"}`}>average best</span>
              </span>
              <span className={`hidden text-[13px] font-semibold md:mt-1.5 md:block ${lead ? "text-teal-light" : "text-teal-dark"}`}>{share}</span>
            </li>
          );
        })}
      </ol>
      {data.strip ? (
        <MonthStrip data={data} areaName={areaName} />
      ) : (
        <p className="mx-4 mt-2 text-[12px] leading-[1.45] text-[#566072] md:mx-[22px] md:mt-3 md:text-[12.5px]">Only the top three months are published.</p>
      )}
      {data.topSpot && (
        <p data-area-top-spot="" className="mx-4 mt-3 text-[13.5px] font-bold leading-[1.45] text-navy md:mx-[22px] md:text-[14px]">
          Most often the best {thing}: {data.topSpot.name}, top on {data.topSpot.days}
          {data.topSpot.totalDays !== null ? ` of ${data.topSpot.totalDays}` : ""} {data.topSpot.days === 1 && data.topSpot.totalDays === null ? "day" : "days"}.
        </p>
      )}
    </>
  );
}

/** Every month's average best score, the top three in teal (G4 record strip). */
function MonthStrip({ data, areaName }: Readonly<{ data: AreaViewBestMonths; areaName: string }>) {
  const strip = data.strip ?? [];
  const max = Math.max(1, ...strip.map((bar) => bar.score100 ?? 0));
  const aria = `Average best ${data.sportLabel} score by month in ${areaName}: ${strip
    .filter((bar) => bar.score100 !== null)
    .map((bar) => `${monthLong(bar.month)} ${bar.score100}`)
    .join(", ")}.`;
  return (
    <div className="mx-4 mt-3 rounded-[14px] border border-line bg-warm-bg px-3 pb-2.5 pt-3 md:mx-[22px] md:rounded-[16px] md:px-4 md:pt-3.5">
      <p className="text-[13px] font-bold text-navy">Every month, average best score</p>
      <div role="img" aria-label={aria} className="mt-2 grid grid-cols-12 items-end gap-1 md:gap-1.5">
        {strip.map((bar) => (
          <div key={bar.month} aria-hidden="true" data-area-month-bar={bar.month} className="flex h-[96px] flex-col items-center justify-end gap-1 md:h-[104px]">
            <span className={`text-[10px] font-bold md:text-[11px] ${bar.top ? "text-teal-dark" : "text-[#566072]"}`}>{bar.score100 ?? ""}</span>
            <span
              className={`block w-full rounded-[4px_4px_2px_2px] ${bar.top ? "bg-teal" : "bg-[#C9D0D6]"}`}
              style={{ height: bar.score100 === null ? 3 : Math.max(4, Math.round((bar.score100 / max) * 60)) }}
            />
            <span className={`text-[10.5px] md:text-[11.5px] ${bar.top ? "font-bold text-navy" : "font-semibold text-[#566072]"}`}>{bar.initial}</span>
          </div>
        ))}
      </div>
      <p className="mt-1.5 text-[11.5px] leading-[1.4] text-[#566072] md:text-[12px]">Teal bars are the top three months. Scores out of 100.</p>
    </div>
  );
}

function EmptyRecord({ note }: Readonly<{ note: string }>) {
  return (
    <div className="mx-4 mt-2.5 flex items-start gap-2.5 rounded-[14px] border border-line bg-warm-bg px-3.5 py-3 md:mx-[22px]">
      <span aria-hidden="true" className="mt-px flex text-teal-dark">
        <G3Icon name="calendar" size={18} />
      </span>
      <p className="text-[13px] leading-[1.45] text-text-body md:text-[14px] md:leading-[1.5]">
        <strong className="text-navy">{note}</strong> There are no published best months yet. They appear once enough daily forecasts back them.
      </p>
    </div>
  );
}

function WindWinners({ data }: Readonly<{ data: AreaViewBestMonths }>) {
  if (data.windWinners.length === 0) return null;
  return (
    <>
      <h3 className="mx-4 mt-[18px] text-[15px] font-bold text-navy md:mx-[22px]">Best beach by wind direction</h3>
      <ul className="mx-4 mt-2 flex flex-col gap-1.5 md:mx-[22px]">
        {data.windWinners.map((winner) => (
          <li key={`${winner.direction}-${winner.slug}`} className="flex min-h-11 items-center gap-2.5 rounded-[12px] bg-warm-bg px-3 py-2">
            <span className="inline-flex h-6 min-w-9 shrink-0 items-center justify-center rounded-full bg-navy px-2 text-[11.5px] font-bold text-white">
              {winner.direction}
            </span>
            <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-navy">{winner.name}</span>
            <span className="shrink-0 text-[12.5px] text-[#566072]">best on {winner.wins} of {winner.days} days</span>
          </li>
        ))}
      </ul>
    </>
  );
}

function LastSeason({ data }: Readonly<{ data: AreaViewBestMonths }>) {
  const last = data.lastSeason;
  if (!last) return null;
  const parts: string[] = [];
  if (last.bestWeekStartISO) parts.push(`the best week started on ${dayMonthLong(last.bestWeekStartISO)}`);
  if (last.topResortName && last.topResortDaysAtTop > 0) {
    parts.push(`${last.topResortName} was the top resort on ${last.topResortDaysAtTop} ${last.topResortDaysAtTop === 1 ? "day" : "days"}`);
  }
  if (parts.length === 0) return null;
  return (
    <p className="mx-4 mt-3 rounded-[12px] bg-warm-bg px-3.5 py-2.5 text-[13px] leading-[1.45] text-text-body md:mx-[22px]">
      <strong className="text-navy">Last season, {last.label}:</strong> {joinList(parts)}.
    </p>
  );
}

// ── Best time curves (older weekly scores) ───────────────────────────────

function runnerUp(curve: AreaViewMonthlyCurve): string | null {
  if (!curve.best) return null;
  const next = curve.months
    .filter((month) => month.score100 !== null && month.month !== curve.best?.month)
    .sort((a, b) => (b.score100 ?? 0) - (a.score100 ?? 0))[0];
  return next && next.score100 !== null ? `${monthShort(next.month)} ${next.score100}` : null;
}

function curveAria(curves: readonly AreaViewMonthlyCurve[]): string {
  const parts = curves.map((curve) => {
    const scored = curve.months.filter((month) => month.score100 !== null);
    return `${curve.label}: ${scored.map((month) => `${monthLong(month.month)} ${month.score100}`).join(", ")}`;
  });
  return `Average score by month from our older weekly records, not comparable with today's GO scores. ${parts.join(". ")}.`;
}

function Curves({ data }: Readonly<{ data: AreaViewBestTime }>) {
  const curves = data.curves.filter((curve) => curve.best !== null);
  if (curves.length === 0) return null;
  const max = Math.max(1, ...curves.flatMap((curve) => curve.months.map((month) => month.score100 ?? 0)));
  return (
    <div className="mx-4 mt-3.5 flex flex-col gap-3.5 md:mx-[22px]">
      <div className="flex items-center justify-between gap-2.5">
        <h3 className="text-[15px] font-bold text-navy md:text-[16px]">Average score by month</h3>
        <span className="whitespace-nowrap text-[12px] text-[#566072]">older scoring</span>
      </div>
      <div role="img" aria-label={curveAria(curves)} className="flex flex-col gap-2.5">
        {curves.map((curve) => (
          <div key={curve.sport} aria-hidden="true" data-area-curve={curve.sport} className="grid grid-cols-[44px_repeat(12,minmax(0,1fr))] items-end gap-[3px]">
            <span className="self-center text-[11.5px] font-bold text-navy">{curve.label}</span>
            {curve.months.map((month) => {
              const best = curve.best?.month === month.month;
              if (month.score100 === null) {
                return (
                  <div key={month.month} className="flex h-[106px] flex-col items-center justify-end">
                    <span className="block h-1 w-full rounded-[2px] bg-[#EDF1EC]" />
                  </div>
                );
              }
              const height = Math.max(4, Math.round((month.score100 / max) * 64));
              return (
                <div key={month.month} className="flex h-[106px] flex-col items-center justify-end gap-[3px]">
                  <span className="text-[10.5px] font-bold text-navy">{month.score100}</span>
                  <span
                    className="block w-full rounded-[5px_5px_2px_2px]"
                    style={{
                      height,
                      background: CURVE_COLOUR[curve.sport],
                      opacity: best ? 1 : 0.55,
                      boxShadow: best ? "0 0 0 2px #FFFFFF, 0 0 0 4px #16233B" : undefined,
                    }}
                  />
                </div>
              );
            })}
          </div>
        ))}
        <div aria-hidden="true" className="-mt-1 grid grid-cols-[44px_repeat(12,minmax(0,1fr))] gap-[3px]">
          <span />
          {MONTH_INITIALS.map((initial, index) => (
            <span key={index} className="text-center text-[10.5px] font-semibold text-[#566072]">{initial}</span>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {curves.map((curve) => {
          const second = runnerUp(curve);
          return curve.best ? (
            <div key={curve.sport} className="rounded-[12px] bg-warm-bg px-3 py-2.5">
              <p className="text-[12px] font-bold text-teal-dark">{CURVE_TILE_LABEL[curve.sport]}</p>
              <p className="mt-[3px] text-[17px] font-bold text-navy">{curve.best.name}</p>
              <p className="mt-0.5 text-[12.5px] leading-[1.35] text-[#566072]">
                {curve.best.score100} average{second ? `, ${second} next` : ""}
              </p>
            </div>
          ) : null;
        })}
      </div>
      <p className="text-[12px] leading-[1.45] text-[#566072]">
        These bars use our older weekly averages for the resorts here. That is older scoring, not comparable with today&apos;s GO scores.
      </p>
    </div>
  );
}

// ── Crowds and lift pass ─────────────────────────────────────────────────

type CrowdLevel = "quiet" | "moderate" | "busy";

const CROWD_COLOUR: Readonly<Record<CrowdLevel, string>> = { quiet: "#0E9E7E", moderate: "#9AA5B5", busy: "#C17B5D" };

/** Pure: a week's crowd band (0 quiet to 1 busy). */
export function crowdLevel(crowd: number): CrowdLevel {
  if (crowd <= 0.3) return "quiet";
  if (crowd >= 0.75) return "busy";
  return "moderate";
}

function weekLabel(week: AreaViewSeasonWeek): string {
  return `week of ${dayMonthLong(week.weekStart)}`;
}

function euros(value: number): string {
  return `€${Math.round(value)}`;
}

function Crowds({ data }: Readonly<{ data: AreaViewBestTime }>) {
  const weeks = data.weeks;
  if (weeks.length === 0) return null;
  const pass = data.pass;
  const first = weeks[0];
  const last = weeks[weeks.length - 1];
  const ariaParts = [
    `Weekly crowd level${pass ? " and lift pass price" : ""}, ${dayMonthLong(first.weekStart)} to ${dayMonthLong(last.weekStart)}.`,
    data.quietestWeek ? `Quietest: ${weekLabel(data.quietestWeek)}.` : "",
    data.busiestWeeks.length > 0 ? `Busiest: weeks of ${joinList(data.busiestWeeks.map(dayMonthLong))}.` : "",
    pass ? `${pass.label} ${euros(pass.minEur)} to ${euros(pass.maxEur)}.` : "",
  ].filter(Boolean);
  const columns = { gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))` };
  const monthLabels = weeks.map((week, index) => {
    const month = monthShort(Number(week.weekStart.slice(5, 7)));
    const previous = index > 0 ? monthShort(Number(weeks[index - 1].weekStart.slice(5, 7))) : "";
    return month !== previous ? month : "";
  });
  const cheapestDiffers = data.cheapestWeek && data.cheapestWeek.weekStart !== data.quietestWeek?.weekStart;
  return (
    <div className="mx-4 mt-[18px] flex flex-col gap-3 md:mx-[22px]">
      <div className="flex items-center justify-between gap-2.5">
        <h3 className="text-[15px] font-bold text-navy md:text-[16px]">Crowds and lift pass</h3>
        <span className="whitespace-nowrap text-[12px] text-[#566072]">Winter {data.seasonLabel}</span>
      </div>
      <div role="img" aria-label={ariaParts.join(" ")}>
        <div aria-hidden="true" className="grid h-[84px] items-end gap-0.5" style={columns}>
          {weeks.map((week) => (
            <span
              key={week.weekStart}
              data-area-crowd={crowdLevel(week.crowd)}
              className="block min-h-1.5 rounded-[4px_4px_2px_2px]"
              style={{ height: `${Math.round(Math.max(0, Math.min(1, week.crowd)) * 100)}%`, background: CROWD_COLOUR[crowdLevel(week.crowd)] }}
            />
          ))}
        </div>
        {pass ? (
          <div aria-hidden="true" className="mt-1.5 grid gap-0.5" style={columns}>
            {weeks.map((week) => (
              <span
                key={week.weekStart}
                className="h-4 rounded-[3px]"
                style={{ background: week.priceEur !== null && Math.round(week.priceEur) === pass.minEur && pass.minEur !== pass.maxEur ? "#5BC0CE" : "#C9D3E0" }}
              />
            ))}
          </div>
        ) : null}
        <div aria-hidden="true" className="mt-1.5 grid gap-0.5 text-[11px] font-semibold text-[#566072]" style={columns}>
          {weeks.map((week, index) => (
            <span key={week.weekStart} className="overflow-visible whitespace-nowrap">{monthLabels[index]}</span>
          ))}
        </div>
      </div>
      <p className="text-[12px] leading-[1.4] text-[#566072]">
        Bar height: how busy that week is, from our crowd model.{pass ? " Strip below: pass price." : ""}
      </p>
      <div aria-hidden="true" className="flex flex-wrap gap-x-3.5 gap-y-1.5 text-[12px] text-[#566072]">
        {(["quiet", "moderate", "busy"] as const).map((level) => (
          <span key={level} className="inline-flex items-center gap-[5px]">
            <span className="size-2.5 rounded-[3px]" style={{ background: CROWD_COLOUR[level] }} />
            {level.charAt(0).toUpperCase() + level.slice(1)}
          </span>
        ))}
        {pass && pass.minEur !== pass.maxEur ? (
          <>
            <span className="inline-flex items-center gap-[5px]">
              <span className="size-2.5 rounded-[3px] bg-teal-light" />
              Pass {euros(pass.minEur)} off-peak
            </span>
            <span className="inline-flex items-center gap-[5px]">
              <span className="size-2.5 rounded-[3px] bg-[#C9D3E0]" />
              Pass up to {euros(pass.maxEur)}
            </span>
          </>
        ) : pass ? (
          <span className="inline-flex items-center gap-[5px]">
            <span className="size-2.5 rounded-[3px] bg-[#C9D3E0]" />
            Pass {euros(pass.minEur)}
          </span>
        ) : null}
      </div>
      <ul className="flex flex-col gap-2">
        {data.quietestWeek ? (
          <Highlight colour={CROWD_COLOUR.quiet} title={`Sweet spot: ${weekLabel(data.quietestWeek)}`}>
            The quietest week of the season.{data.quietestWeek.priceEur !== null ? ` Pass ${euros(data.quietestWeek.priceEur)}.` : ""}
          </Highlight>
        ) : null}
        {cheapestDiffers && data.cheapestWeek && data.cheapestWeek.priceEur !== null ? (
          <Highlight colour="#5BC0CE" title={`Best value: ${weekLabel(data.cheapestWeek)}`}>
            The cheapest pass of the season at {euros(data.cheapestWeek.priceEur)}, and {crowdLevel(data.cheapestWeek.crowd)} on the slopes.
          </Highlight>
        ) : null}
        {data.busiestWeeks.length > 0 ? (
          <Highlight colour={CROWD_COLOUR.busy} title="Busiest weeks">
            Weeks of {joinList(data.busiestWeeks.map(dayMonthLong))}.
          </Highlight>
        ) : null}
      </ul>
      {pass ? (
        <p className="text-[12px] leading-[1.4] text-[#566072]">
          {pass.label}, {data.seasonLabel} dates and prices. Next season&apos;s calendar will shift slightly.
        </p>
      ) : null}
      <div>
        <Link
          href={data.href}
          className="group inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap text-[14.5px] font-semibold text-teal-dark no-underline hover:text-teal"
        >
          Find the best time to go skiing
          <HubArrow />
        </Link>
      </div>
    </div>
  );
}

function Highlight({ colour, title, children }: Readonly<{ colour: string; title: string; children: ReactNode }>) {
  return (
    <li className="flex gap-2.5">
      <span aria-hidden="true" className="mt-[5px] size-2.5 shrink-0 rounded-full" style={{ background: colour }} />
      <span className="flex flex-col gap-px">
        <span className="text-[14px] font-bold text-navy">{title}</span>
        <span className="text-[13px] leading-[1.4] text-[#566072]">{children}</span>
      </span>
    </li>
  );
}

// ── Awards ───────────────────────────────────────────────────────────────

/** Pure: the one-line summary under the awards heading ("Monthly and season surf awards ..."). */
export function awardsSummary(awards: AreaViewSportAwards, areaName: string, unit: AreaViewUnit): string {
  return `Monthly and season ${awards.sportLower} awards won by ${areaName} ${unit}.`;
}

/** Pure: "Also won here in 2026: Vis Capital (Diving, Bude Summerleaze, Jul 2026) and ..." or null. */
export function awardsAlsoLine(awards: AreaViewSportAwards): string | null {
  if (awards.others.length === 0) return null;
  // The winner without its locality ("Chapel Porth", not "Chapel Porth, St Agnes").
  const items = awards.others.slice(0, 3).map((award) => `${award.name} (${award.sportLabel}, ${award.winnerName.split(", ")[0]}, ${award.periodLabel})`);
  const more = awards.others.length - items.length;
  return `Also won here in ${awards.year}: ${joinList(more > 0 ? [...items, `${more} more`] : items)}.`;
}

function awardPill(award: AreaViewAward): Readonly<{ label: string; className: string }> {
  if (award.fun) return { label: award.kindLabel === "Roast" ? "Roast" : "Fun", className: "bg-[#F5E8E2] text-[#8E4C30]" };
  return { label: award.period === "season" ? "Season" : "Monthly", className: "bg-[#E3F4EF] text-[#0A6E58]" };
}

function AwardRow({ award }: Readonly<{ award: AreaViewAward }>) {
  const pill = awardPill(award);
  const stat = [award.stat, award.statLabel].filter(Boolean).join(" ");
  return (
    <li>
      <Link
        href={award.href}
        aria-label={`${award.name}, ${award.kindLabel}, ${award.sportLabel}, ${award.periodLabel}: ${award.winnerName}${stat ? `, ${stat}` : ""}`}
        className="group flex min-h-[60px] items-center gap-2.5 rounded-[12px] p-2 no-underline transition-colors hover:bg-[#F4F8F7] md:min-h-[66px] md:px-2.5"
      >
        <span className="flex min-w-0 flex-1 flex-col gap-px">
          <span className="flex items-center gap-1.5">
            <span className="min-w-0 truncate text-[14.5px] font-bold text-navy md:text-[15px]">{award.name}</span>
            <span className={`shrink-0 rounded-full px-[7px] text-[10.5px] font-bold leading-[18px] ${pill.className}`}>{pill.label}</span>
          </span>
          <span className="text-[12px] font-semibold text-teal-dark">{award.sportLabel} · {award.periodLabel}</span>
          <span className="text-[12.5px] text-[#566072]">{award.winnerName}{stat ? `, ${stat}` : ""}</span>
        </span>
        <span className="flex text-teal-dark">
          <HubChevron direction="right" />
        </span>
      </Link>
    </li>
  );
}

function AwardGroups({ awards }: Readonly<{ awards: AreaViewSportAwards }>) {
  const groups: readonly Readonly<{ key: string; single: string; plural: string; items: readonly AreaViewAward[] }>[] = [
    { key: "season", single: "Season award", plural: "Season awards", items: awards.items.filter((award) => award.period === "season" && !award.fun) },
    { key: "month", single: "Monthly award", plural: "Monthly awards", items: awards.items.filter((award) => award.period === "month" && !award.fun) },
    { key: "fun", single: "Fun award", plural: "Fun awards", items: awards.items.filter((award) => award.fun) },
  ];
  const also = awardsAlsoLine(awards);
  return (
    <>
      {awards.items.length === 0 ? (
        <p data-area-awards-none="" className="mx-4 mt-2 rounded-[14px] border border-dashed border-[#CBD3CB] px-3.5 py-3 text-[13.5px] leading-[1.45] text-text-body md:mx-[22px]">
          No {awards.sportLower} award won here yet this year, monthly or season.
        </p>
      ) : groups.filter((group) => group.items.length > 0).map((group) => (
        <div key={group.key} data-area-award-group={group.key}>
          <G3GroupLabel className="mx-4 mt-2.5 md:mx-[22px]">{group.items.length === 1 ? group.single : group.plural}</G3GroupLabel>
          <ul className="mx-2 mt-0.5">
            {group.items.map((award) => <AwardRow key={`${award.id}-${award.winnerSlug}-${award.periodLabel}`} award={award} />)}
          </ul>
        </div>
      ))}
      {also && <p className="mx-4 mt-2 text-[12.5px] leading-[1.45] text-[#566072] md:mx-[22px] md:text-[13px]">{also}</p>}
      <div className="px-3 pb-3.5 pt-2.5 md:px-4 md:pb-4">
        <G3ActionLink href={awards.allHref}>{awards.allLabel}</G3ActionLink>
      </div>
    </>
  );
}

export type AreaAwardsBodyProps = Readonly<{
  areaName: string;
  unit: AreaViewUnit;
  awards: AreaViewSportAwards;
}>;

/** The awards card's content for one sport (the desktop aside card holds one per sport). */
export function AreaAwardsBody({ areaName, unit, awards }: AreaAwardsBodyProps) {
  const headingId = `area-awards-h-${awards.sport}`;
  return (
    <div data-area-awards={awards.sport}>
      <G3CardHeader id={headingId} icon="trophy" title={`Awards won here in ${awards.year}`} lead={awardsSummary(awards, areaName, unit)} />
      <AwardGroups awards={awards} />
    </div>
  );
}

/** The awards inside the record (phones), for one sport. */
function AwardsInline({ areaName, unit, awards, className = "" }: AreaAwardsBodyProps & Readonly<{ className?: string }>) {
  return (
    <div data-area-awards-inline={awards.sport} className={className}>
      <h3 className="mx-4 mt-[18px] flex items-center gap-2 text-[15px] font-bold text-navy md:mx-[22px] md:mt-6 md:text-[16px]">
        <span aria-hidden="true" className="flex text-teal-dark">
          <G3Icon name="trophy" size={18} />
        </span>
        Awards won here in {awards.year}
      </h3>
      <p className="mx-4 mt-[3px] text-[12.5px] leading-[1.45] text-[#566072] md:mx-[22px] md:text-[13px]">{awardsSummary(awards, areaName, unit)}</p>
      <AwardGroups awards={awards} />
    </div>
  );
}

// ── Record body (one per sport) ──────────────────────────────────────────

export type AreaRecordBodyProps = Readonly<{
  areaName: string;
  kind: AreaKind;
  /** The sport panel's unit, for "a beach at 70+" and the awards line. */
  unit: AreaViewUnit;
  bestMonths: AreaViewBestMonths;
  /** Mountain areas, the page's own sport only: best-time curves, crowds and pass prices. */
  bestTime: AreaViewBestTime | null;
  /** The sport's awards, shown inside the record below `lg` (the desktop aside has its card). */
  awards: AreaViewSportAwards | null;
}>;

function leadLine(areaName: string, bestMonths: AreaViewBestMonths): string {
  if (bestMonths.months.length > 0 && bestMonths.spanLabel) return `When ${areaName} is at its best for ${bestMonths.sportLabel}, from ${bestMonths.spanLabel}.`;
  return `When ${areaName} is at its best for ${bestMonths.sportLabel}.`;
}

/**
 * "The PeakWave record" for one sport (G4 item 3: the record follows the toggle): best months
 * (top three, the 12-month strip and the spot most often best when the insights carry them) or
 * an honest empty state, wind direction winners (surf), last season (piste), the mountain
 * best-time extras (the page's own sport), and on phones the sport's awards.
 */
export function AreaRecordBody({ areaName, kind, unit, bestMonths, bestTime, awards }: AreaRecordBodyProps) {
  const titleId = `area-best-h-${bestMonths.sport}`;
  const hasMonths = bestMonths.months.length > 0;
  const mountainExtras = kind === "mountain" && bestTime !== null;
  return (
    <div data-area-record={bestMonths.sport}>
      <G3CardHeader id={titleId} icon="bars" title="The PeakWave record" lead={leadLine(areaName, bestMonths)} />
      {hasMonths ? (
        <BestMonthsList data={bestMonths} areaName={areaName} unit={unit} />
      ) : (
        <EmptyRecord note={bestMonths.emptyNote ?? "We are still building this area's season record."} />
      )}
      <WindWinners data={bestMonths} />
      <LastSeason data={bestMonths} />
      {mountainExtras ? <Curves data={bestTime} /> : null}
      {mountainExtras ? <Crowds data={bestTime} /> : null}
      {awards ? <AwardsInline areaName={areaName} unit={unit} awards={awards} className="lg:hidden" /> : null}
      <div aria-hidden="true" className={`h-4 md:h-[22px] ${awards ? "hidden lg:block" : ""}`} />
    </div>
  );
}
